/**
 * Security Controls — typed API client over `apiClient` (never a bare fetch).
 *
 * Base path: `/api/tools/security-controls`. Every write route enforces idempotency, so each write
 * sends the caller's `Idempotency-Key` (ADR-005).
 */
import apiClient from '@/api/client';
import type {
  ActivationResult,
  ApplyMoveResult,
  DismissMoveResult,
  EntriesResponse,
  PendingMovesResponse,
  RefreshResult,
  SaveRequest,
  SaveResult,
  TreeResponse,
} from './types';

const BASE = '/api/tools/security-controls';

const withKey = (key: string) => ({ headers: { 'Idempotency-Key': key } });

export async function getTree(): Promise<TreeResponse> {
  const { data } = await apiClient.get<TreeResponse>(`${BASE}/tree`);
  return data;
}

/** `params` lets axios encode the path's backslashes. */
export async function getEntries(path: string): Promise<EntriesResponse> {
  const { data } = await apiClient.get<EntriesResponse>(`${BASE}/entries`, { params: { path } });
  return data;
}

export async function saveEntries(body: SaveRequest, idempotencyKey: string): Promise<SaveResult> {
  const { data } = await apiClient.post<SaveResult>(`${BASE}/save`, body, withKey(idempotencyKey));
  return data;
}

export async function inactivatePath(path: string, idempotencyKey: string): Promise<ActivationResult> {
  const { data } = await apiClient.post<ActivationResult>(`${BASE}/inactivate`, { path }, withKey(idempotencyKey));
  return data;
}

export async function activatePath(path: string, idempotencyKey: string): Promise<ActivationResult> {
  const { data } = await apiClient.post<ActivationResult>(`${BASE}/activate`, { path }, withKey(idempotencyKey));
  return data;
}

/** C6: re-reads the report catalogue. Every click is a new operation, so the caller passes a new key. */
export async function refreshSecurity(idempotencyKey: string): Promise<RefreshResult> {
  const { data } = await apiClient.post<RefreshResult>(`${BASE}/refresh`, {}, withKey(idempotencyKey));
  return data;
}

export async function getPendingMoves(): Promise<PendingMovesResponse> {
  const { data } = await apiClient.get<PendingMovesResponse>(`${BASE}/pending-moves`);
  return data;
}

/** The id is a report key, so it is encoded for the URL. */
export async function applyPendingMove(id: string, idempotencyKey: string): Promise<ApplyMoveResult> {
  const { data } = await apiClient.post<ApplyMoveResult>(
    `${BASE}/pending-moves/${encodeURIComponent(id)}/apply`,
    {},
    withKey(idempotencyKey),
  );
  return data;
}

export async function dismissPendingMove(id: string, idempotencyKey: string): Promise<DismissMoveResult> {
  const { data } = await apiClient.post<DismissMoveResult>(
    `${BASE}/pending-moves/${encodeURIComponent(id)}/dismiss`,
    {},
    withKey(idempotencyKey),
  );
  return data;
}
